import { useState, useEffect, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Loader2, CheckCircle, Smartphone } from "lucide-react";
import logoMindsup from "@/assets/logo-mindsup.svg";
import { toast } from "sonner";

function hasSupabaseRecoveryInHash(): boolean {
  const hash = window.location.hash;
  return (
    hash.includes("type=recovery") ||
    hash.includes("type=invite") ||
    hash.includes("type=magiclink")
  );
}

function appPasswordResetDeepLink(token: string): string {
  const scheme = (import.meta.env.VITE_APP_DEEPLINK_SCHEME as string | undefined)?.trim() || "mindsup";
  return `${scheme}://reset-password?token=${encodeURIComponent(token)}`;
}

function ResetPasswordOpenInAppNotice({ openInAppHref }: { openInAppHref: string }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-md space-y-8">
        <div className="flex flex-col items-center gap-3">
          <img src={logoMindsup} alt="MindsUp" className="h-12" />
        </div>
        <Card className="border-border/50 shadow-lg">
          <CardHeader className="pb-2 text-center">
            <div className="mx-auto mb-2 flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
              <Smartphone className="h-7 w-7 text-primary" aria-hidden />
            </div>
            <h1
              className="text-lg font-semibold"
              style={{ fontFamily: "'DM Sans', sans-serif" }}
            >
              Ouvrir depuis votre téléphone
            </h1>
          </CardHeader>
          <CardContent className="space-y-4 text-center text-muted-foreground">
            <p className="text-sm leading-relaxed">
              Pour réinitialiser votre mot de passe, ouvrez ce lien sur votre smartphone avec
              l’application MindsUp installée. L’application reprendra le lien et vous guidera
              pour choisir un nouveau mot de passe.
            </p>
            <Button asChild className="w-full">
              <a href={openInAppHref}>Ouvrir dans l’application MindsUp</a>
            </Button>
            <p className="text-sm leading-relaxed">
              Si vous avez ouvert cette page sur un ordinateur, utilisez plutôt le même lien
              depuis l’e-mail sur votre téléphone, ou copiez l’adresse de la barre d’adresse et
              ouvrez-la sur votre mobile.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default function ResetPassword() {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [isRecovery, setIsRecovery] = useState(false);
  const [checking, setChecking] = useState(true);
  const navigate = useNavigate();

  /** Lien e-mail « mot de passe oublié » app (query `token=`) — pas le flux Supabase admin (hash). */
  const isAppEmailResetLink = useMemo(() => {
    const token = new URLSearchParams(window.location.search).get("token")?.trim();
    if (!token) return false;
    return !hasSupabaseRecoveryInHash();
  }, []);

  useEffect(() => {
    // Listen for auth events to detect recovery/invite flow
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event) => {
        if (event === "PASSWORD_RECOVERY" || event === "SIGNED_IN") {
          setIsRecovery(true);
        }
        setChecking(false);
      }
    );

    // Also check URL hash for recovery token
    const hash = window.location.hash;
    if (hash.includes("type=recovery") || hash.includes("type=invite") || hash.includes("type=magiclink")) {
      setIsRecovery(true);
    }

    // Timeout fallback
    const timeout = setTimeout(() => {
      setChecking(false);
      // If we have a session, allow password change
      supabase.auth.getSession().then(({ data: { session } }) => {
        if (session) setIsRecovery(true);
      });
    }, 2000);

    return () => {
      subscription.unsubscribe();
      clearTimeout(timeout);
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (password.length < 6) {
      setError("Le mot de passe doit contenir au moins 6 caractères.");
      return;
    }
    if (password !== confirmPassword) {
      setError("Les mots de passe ne correspondent pas.");
      return;
    }

    setLoading(true);
    const { error: updateError } = await supabase.auth.updateUser({ password });

    if (updateError) {
      setError(updateError.message);
    } else {
      setSuccess(true);
      toast.success("Mot de passe défini avec succès !");

      // Send password changed notification via Brevo (best effort)
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.access_token) {
          fetch(
            `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/notify-password-changed`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${session.access_token}`,
              },
              body: JSON.stringify({}),
            }
          ).catch(() => {}); // fire and forget
        }
      } catch {
        // optional recovery side-effects may fail silently
      }

      setTimeout(() => navigate("/login"), 2000);
    }
    setLoading(false);
  };

  if (isAppEmailResetLink) {
    const token = new URLSearchParams(window.location.search).get("token")?.trim() ?? "";
    return <ResetPasswordOpenInAppNotice openInAppHref={appPasswordResetDeepLink(token)} />;
  }

  if (checking) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isRecovery) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-4">
        <div className="w-full max-w-md space-y-8">
          <div className="flex flex-col items-center gap-3">
            <img src={logoMindsup} alt="MindsUp" className="h-12" />
          </div>
          <Card className="border-border/50 shadow-lg">
            <CardContent className="pt-6">
              <p className="text-center text-muted-foreground">
                Ce lien est invalide ou a expiré. Veuillez demander un nouveau lien de réinitialisation.
              </p>
              <Button className="w-full mt-4" onClick={() => navigate("/login")}>
                Retour à la connexion
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-4">
        <div className="w-full max-w-md space-y-8">
          <div className="flex flex-col items-center gap-3">
            <img src={logoMindsup} alt="MindsUp" className="h-12" />
          </div>
          <Card className="border-border/50 shadow-lg">
            <CardContent className="pt-6 text-center space-y-4">
              <CheckCircle className="h-12 w-12 text-primary mx-auto" />
              <p className="font-medium">Mot de passe défini avec succès !</p>
              <p className="text-sm text-muted-foreground">Redirection vers la page de connexion…</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-md space-y-8">
        <div className="flex flex-col items-center gap-3">
          <img src={logoMindsup} alt="MindsUp" className="h-12" />
          <p className="text-sm text-muted-foreground">Espace d'administration</p>
        </div>

        <Card className="border-border/50 shadow-lg">
          <CardHeader className="pb-4">
            <h2 className="text-lg font-semibold text-center" style={{ fontFamily: "'DM Sans', sans-serif" }}>
              Définir votre mot de passe
            </h2>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="password">Nouveau mot de passe</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  autoComplete="new-password"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirmer le mot de passe</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  autoComplete="new-password"
                />
              </div>

              {error && <p className="text-sm text-destructive">{error}</p>}

              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Définir le mot de passe
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
